import React, {useEffect, useState} from 'react';
import Input from '../../forms/inputs/Input';
import Select from '../../forms/inputs/Select';
import ButtonIcon from '../../forms/buttons/Button.icon';

import './Users.create.addressList.scss'
import SelectCountry from "../../forms/inputs/Select.country";
import {getAddressFormatFields} from "../../utils/address/addressFormat";
import { useTranslation } from 'react-i18next';
import AuditTrail from "../../services/interactions/audit.trail";

function Field({ event, i, entry, field, error, handleAddressChange, required, propertyValidation}) {
    const {t} = useTranslation(['users'])
    const conditionalMissing = (condition, ownValue) => {
        if (condition && !ownValue) {
            return true
        }
        return false;
    }

    const TYPE_OPTIONS = [
        { label: t('address-type.home'), value: 'Home' },
        { label: t('address-type.work'), value: 'Work' },
        { label: t('other'), value: 'Other' }
    ]

    const type = field.type;
    if (type === 'line') {
        return (
            <Input error={(error && !propertyValidation(i, 'line', entry)) || conditionalMissing(entry.zip, entry.line)} required={required} value={entry.line} label={field.label} onChange={handleAddressChange(i, 'line')} event={event} />
        )
    }
    if (type === 'type') {
        return (
            <Select required={required} value={entry.type} label={field.label} options={TYPE_OPTIONS} onChange={handleAddressChange(i, 'type')} event={event} />
        )
    }
    if (type === 'lineTwo') {
        return (
            <Input value={entry.lineTwo} label={field.label} onChange={handleAddressChange(i, 'lineTwo')} event={event} />
        )
    }
    if (type === 'references') {
        return (
            <Input value={entry.references} label={field.label} onChange={handleAddressChange(i, 'references')} event={event} />
        )
    }
    if (type === 'zip') {
        return (
            <Input error={(error && !propertyValidation(i, 'zip', entry)) || conditionalMissing(entry.city, entry.zip)} required={required} value={entry.zip} label={field.label} onChange={handleAddressChange(i, 'zip')} event={event} />
        )
    }
    if (type === 'city') {
        return (
            <Input required={required} error={(error && !propertyValidation(i, 'city', entry)) || conditionalMissing(entry.state, entry.city)} value={entry.city} label={field.label} onChange={handleAddressChange(i, 'city')} event={event} />
        )
    }
    if (type === 'state') {
        return (
            <Input required={required} error={(error && !propertyValidation(i, 'state', entry)) || conditionalMissing(entry.city, entry.state)} value={entry.state} label={field.label} onChange={handleAddressChange(i, 'state')} event={event} />
        )
    }
    if (type === 'empty') {
        return (
            <div>
            </div>
        )
    }

    return '';
}

export default function CreateUserAddressList({ event, error, defaultCountry, address, onChange, arrayRequired }) {
    const [init, setInit] = useState(false);
    const {t} = useTranslation(['users'])
    const emptyAddress = () => {
        return ({
            country: defaultCountry? defaultCountry : '',
            line: '',
            lineTwo: '',
            type: 'Work',
            references: '',
            zip: '',
            city: '',
            state: '',
            valid: true
        })
    }

    // Validation helpers
    let _addressValidation = [];

    const _address = (address && address.length ? address : [emptyAddress()]);

    useEffect(() => {
        if (init) {
            setInit(true);
            onChange(JSON.parse(JSON.stringify(_address)));
        }
    }, [address])

    const propertyValidation = (index, property, item, hasValue) => {
        const value = item[property];
        const _isRequired = (index+1) <= arrayRequired;
        let zipFormat = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
        if (item.country) {
            const fields = getAddressFormatFields(item.country.toUpperCase());
            const fieldZip = fields.find((f) => f.key === 'postalCode' && f.zip);
            if (fieldZip) {
                zipFormat = new RegExp(fieldZip['zip']);
            }
        }
        let valid;
        switch (property) {
            case "zip":
                if ((!_isRequired && !hasValue) && !(!!value)) {
                    return true;
                }
                valid = zipFormat.test(value);
                break;
            case "country":
            case "line":
            case "city":
            case "state":
                if ((!_isRequired && !hasValue) && !(!!value)) {
                    return true;
                }
                valid = (value && value.length !== 0);
                break;
            default:
                valid = true;
                break;
        }
        return valid;
    }

    const addressValidation = (ind) => {
        let hasValue = false;
        _addressValidation.forEach((property) => {
            if(property !== 'country' && !!_address[ind][property]){
                hasValue = true;
            }
        });
        let _valid = true;
        _addressValidation.forEach((property) => {
            let valid = propertyValidation(ind, property, _address[ind], hasValue);
            if(!valid){
                _valid = false;
            }
        });
        return _valid;
    }

    const handleAddressChange = (index, property) => {
        return (value) => {
            _address[index][property] = value;
            _address[index]["valid"] = addressValidation(index);
            onChange(JSON.parse(JSON.stringify(_address)));
        }
    }

    const removeAddress = (i) => {
        return () => {
            AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'button remove address', value: _address[i]});
            _address.splice(i, 1);
            onChange(JSON.parse(JSON.stringify(_address)));
        }
    }

    const addNewAddress = () => {
        AuditTrail.sendLog(event, {tag: 'button', event: 'onClick', action: 'button add new address'});
        _address.push(emptyAddress());
        onChange(JSON.parse(JSON.stringify(_address)));
    }

    const getFieldsAddress = (country) => {
        if (!country) {
            return [];
        }
        const fields = getAddressFormatFields(country.toUpperCase());
        _addressValidation = ['country','line'];

        const fieldLine = {label: t('address-data.address-line'), type: 'line'};
        const fieldType = {label: t('address-data.type'), type: 'type'};
        const fieldlineTwo = {label: t('address-data.address-line') + ' 2', type: 'lineTwo'};
        const fieldReferences = {label: t('address-data.directions'), type: 'references'};
        const fieldZip = {label: t('address-data.zip-code'), type: 'zip'};
        const fieldCity = {label: t('address-data.city'), type: 'city'};
        const fieldState = {label: t('address-data.state'), type: 'state'};
        const fieldEmpty = {label: '', type: 'empty'};

        let addressFields = [fieldLine, fieldType, fieldlineTwo, fieldReferences];

        // Display fields for country
        for (let i = 0; i < fields.length; i++) {
            const field = fields[i];
            if (field.key === 'postalCode') {
                if (field.label !== 'postalCode') {
                    fieldZip.label = field.label;
                }
                addressFields.push(fieldZip);
                _addressValidation.push('zip');
            }
            if (field.key === 'locality') {
                if (field.label !== 'locality') {
                    fieldCity.label = field.label;
                }
                addressFields.push(fieldCity);
                _addressValidation.push('city');
            }
            if (field.key === 'administrativeArea') {
                if (field.label !== 'administrativeArea') {
                    fieldState.label = field.label;
                }
                if (country === 'ae') {
                    fieldState.label = t('address-data.emirate');
                }
                addressFields.push(fieldState);
                _addressValidation.push('state');
            }
        }
        addressFields.push(fieldEmpty);
        return addressFields;
    }

    const isRequired = (index) => {
        return (index+1) <= arrayRequired;
    }

    return _address.map((entry, i) => (
        <div key={i}>
            <div className="_addressRow two">
                {
                    i > 0 ?
                        <div className="_addressLabel">
                            <h3>Address {i+1}</h3>
                        </div>
                        : ''
                }
                <SelectCountry error={(error && !propertyValidation(i ,'country', entry))} required={isRequired(i)} value={entry.country} label={t('address-data.country')}
                               onChange={handleAddressChange(i, 'country')} placeholder={t('address-data.select-a-country')} event={event} />
                <div></div>
                {
                    getFieldsAddress(entry.country).map((field, index) => (
                        <Field event={event} key={i+'_'+field.type} i={i} entry={entry} field={field} error={error} handleAddressChange={handleAddressChange} required={isRequired(i)} propertyValidation={propertyValidation} />
                    ))
                }
                {
                    i > 0 ?
                        <div className="_addressDelete">
                            <div>
                                <span className="material-icons _deleteButton">delete</span>
                                <span onClick={removeAddress(i)}>Delete address {i+1}</span>
                            </div>
                        </div>
                        : ''
                }
                {
                    (_address.length - 1) === i ?
                        <div className="actionButton">
                            <ButtonIcon handleClick={addNewAddress} size={34}>
                                <span className="material-icons">
                                    add
                            </span>
                            </ButtonIcon>
                            <p>{t('address-data.add-address')}</p>
                        </div>
                        : <div className="spacing"></div>
                }
            </div>
            {
                 i+1 < _address.length? <div className="divider"></div> : ''
            }
        </div>
    ))
}
